import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";

/**
 * Polls /api/payments/status/{session_id} until payment_status === "paid"
 * (or until a few failed attempts), then shows success + refreshes user tokens.
 */
export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refresh } = useAuth();
  const [state, setState] = useState("polling"); // polling | paid | failed | timeout
  const [tx, setTx] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        setTx(data);
        if (data.payment_status === "paid") {
          setState("paid");
          await refresh();
          toast.success("Tokens added to your wallet!");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState("failed");
          return;
        }
      } catch (e) {
        if (cancelled) return;
      }
      attempts.current += 1;
      if (attempts.current > 15) {
        setState("timeout");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="grid-bg min-h-[calc(100vh-73px)] flex items-center">
      <div className="max-w-xl mx-auto w-full px-6">
        <div className="card-surface p-10 text-center" data-testid="payment-result-card">
          {state === "polling" && (
            <>
              <Loader2 className="mx-auto text-[#FBBF24] mb-4 animate-spin" size={44} />
              <h1 className="font-display font-extrabold text-3xl tracking-tighter mb-2">
                Confirming your payment…
              </h1>
              <p className="text-zinc-400 text-sm">
                Hang tight — Stripe is settling the transaction.
              </p>
            </>
          )}
          {state === "paid" && (
            <>
              <CheckCircle2 className="mx-auto text-[#22C55E] mb-4" size={56} />
              <h1 className="font-display font-extrabold text-3xl tracking-tighter mb-2">
                Payment confirmed!
              </h1>
              <p className="text-zinc-400 text-sm mb-8">
                Your tokens are already in your wallet. Time to bid.
              </p>
              <div className="flex items-center justify-center gap-3">
                <Link to="/" className="btn-bid text-sm">
                  Start bidding <ArrowRight className="inline ml-1" size={14} />
                </Link>
                <Link to="/wallet" className="btn-outline text-sm">
                  View wallet
                </Link>
              </div>
            </>
          )}
          {state === "failed" && (
            <>
              <XCircle className="mx-auto text-[#EF4444] mb-4" size={56} />
              <h1 className="font-display font-extrabold text-3xl tracking-tighter mb-2">
                Payment did not complete
              </h1>
              <p className="text-zinc-400 text-sm mb-8">
                Nothing was charged. Try another pack or payment method.
              </p>
              <Link to="/wallet" className="btn-bid text-sm">
                Back to wallet
              </Link>
            </>
          )}
          {state === "timeout" && (
            <>
              <Loader2 className="mx-auto text-zinc-500 mb-4" size={44} />
              <h1 className="font-display font-extrabold text-3xl tracking-tighter mb-2">
                Still processing…
              </h1>
              <p className="text-zinc-400 text-sm mb-8">
                Your bank is taking a moment. Tokens will appear within a few minutes — refresh your
                wallet.
              </p>
              <Link to="/wallet" className="btn-bid text-sm">
                Open wallet
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
